import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchCMSPage } from '../../store/slices/cmsSlice';

const CMSPage = ({ pageType, slug }) => {
  const dispatch = useAppDispatch();
  const pageData = useAppSelector((state) => state.cms.pages[pageType]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    title: '',
    message: '',
    agreeToPrivacy: false
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    dispatch(fetchCMSPage({ pageType, slug }));
  }, [dispatch, pageType, slug]);

  // Update document title
  useEffect(() => {
    if (pageData?.data?.page_name) {
      document.title = `${pageData.data.page_name} | JetScanner`;
    }
  }, [pageData]);

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Validate email in real-time
  const validateEmail = (value) => {
    const newErrors = { ...errors };

    if (!value || !value.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(value)) {
      newErrors.email = 'Please enter a valid email address';
    } else {
      delete newErrors.email;
    }

    setErrors(newErrors);
  };

  // Validate entire form on submit
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || !formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData.email || !formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.title || !formData.title.trim()) {
      newErrors.title = 'Message title is required';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Title must be at least 3 characters';
    }

    if (!formData.message || !formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    if (!formData.agreeToPrivacy) {
      newErrors.agreeToPrivacy = 'Please agree to the privacy policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Real-time validation only for email - show immediately as user types
    if (name === 'email') {
      setTouched(prev => ({
        ...prev,
        email: true
      }));
      validateEmail(value);
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    setTouched({
      name: true,
      email: true,
      title: true,
      message: true,
      agreeToPrivacy: true
    });

    // Validate entire form
    if (!validateForm()) {
      setFormStatus({ type: 'error', message: 'Please fix the errors above' });
      return;
    }

    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      // TODO: Implement actual API call when backend endpoint is ready
      // await api.post('/contact', formData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setFormStatus({ 
        type: 'success', 
        message: 'Thank you for your message! We will get back to you soon.' 
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        title: '',
        message: '',
        agreeToPrivacy: false
      });
      setErrors({});
      setTouched({});
    } catch (error) {
      setFormStatus({ 
        type: 'error', 
        message: 'Failed to send message. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (pageData?.loading || !pageData) {
    return (
      <div className="bg-jet-gray-100 pt-[80px] md:pt-[90px] pb-8 px-3 sm:px-5">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
            <p className="text-jet-gray-600">Loading content...</p>
          </div>
        </div>
      </div>
    );
  }

  if (pageData?.error) {
    return (
      <div className="bg-jet-gray-100 pt-[80px] md:pt-[90px] pb-8 px-3 sm:px-5">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-jet-gray-800 mb-2">Content Not Found</h2>
            <p className="text-jet-gray-600">{pageData.error}</p>
          </div>
        </div>
      </div>
    );
  }

  const { page_name, page } = pageData.data;
  const isContactPage = pageType === 'contact-us';

  // Render contact page with form on left and content on right
  if (isContactPage) {
    return (
      <div className="bg-jet-gray-100 pt-[80px] md:pt-[90px] pb-8 px-3 sm:px-5">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-jet-gray-800 mb-8 text-center">
            {page_name}
          </h1>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Contact Form */}
            <div className="bg-white rounded-lg shadow-jet p-6 md:p-8">
              <h2 className="text-2xl font-semibold text-jet-gray-800 mb-6">
                Send Us a Message
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Your Name"
                    required
                    className="w-full px-4 py-3 border border-jet-gray-300 rounded-lg focus:ring-2 focus:ring-jet-blue focus:border-transparent outline-none transition-all"
                  />
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Your Email"
                    required
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all ${
                      touched.email && errors.email
                        ? 'border-red-500 focus:ring-red-200'
                        : touched.email && !errors.email && formData.email
                        ? 'border-green-500 focus:ring-green-200'
                        : 'border-jet-gray-300 focus:ring-jet-blue'
                    }`}
                  />
                  {touched.email && errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                  {touched.email && !errors.email && formData.email && (
                    <p className="text-green-600 text-sm mt-1">✓ Email is valid</p>
                  )}
                </div>

                <div>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Message Title"
                    required
                    className="w-full px-4 py-3 border border-jet-gray-300 rounded-lg focus:ring-2 focus:ring-jet-blue focus:border-transparent outline-none transition-all"
                  />
                </div>

                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Write your message"
                    required
                    rows="6"
                    className="w-full px-4 py-3 border border-jet-gray-300 rounded-lg focus:ring-2 focus:ring-jet-blue focus:border-transparent outline-none transition-all resize-none"
                  />
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    name="agreeToPrivacy"
                    id="agreeToPrivacy"
                    checked={formData.agreeToPrivacy}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`mt-1 h-4 w-4 rounded focus:ring-jet-blue cursor-pointer ${
                      formData.agreeToPrivacy
                        ? 'bg-jet-blue border-jet-blue'
                        : 'border-jet-gray-300'
                    }`}
                  />
                  <label htmlFor="agreeToPrivacy" className="ml-2 text-sm text-jet-gray-600">
                    I agree to your privacy policy
                  </label>
                </div>

                {formStatus.message && (
                  <div className={`p-4 rounded-lg ${
                    formStatus.type === 'success' 
                      ? 'bg-green-50 text-green-800 border border-green-200' 
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}>
                    {formStatus.message}
                  </div>
                )}

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </form>
            </div>

            {/* Right Column - Content */}
            <div className="bg-white rounded-lg shadow-jet p-6 md:p-8">
              <div 
                className="cms-content prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: page.page_content }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default layout for other CMS pages
  return (
    <div className="bg-jet-gray-100 pt-[80px] md:pt-[90px] pb-8 px-3 sm:px-5">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-jet p-6 md:p-8">
          <h1 className="text-3xl md:text-4xl font-bold text-jet-gray-800 mb-6">
            {page_name}
          </h1>
          
          <div 
            className="cms-content prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: page.page_content }}
          />
        </div>
      </div>
    </div>
  );
};

export default CMSPage;
